import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Skeleton, Typography } from 'antd';
import cn from 'classnames';

import { Card } from '@/shared/ui/card';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import { TradingPlatformStatisticPieChart } from '../trading-platform-statistic-pie-chart/trading-platform-statistic-pie-chart';

import cls from './trading-platform-statistic-card-legacy.module.css';

interface TradingPlatformStatisticCardLegacyCommon {
    isLoading: boolean;
    preferredColor?: 'up' | 'down' | undefined;
    titleKey: string;
}

interface TradingPlatformStatisticCardLegacyAsValueProps
    extends TradingPlatformStatisticCardLegacyCommon {
    hasColor?: boolean;
    hasPrefix?: boolean;
    value: string;
    winLoss?: never;
    winPercent?: never;
}

interface TradingPlatformStatisticCardLegacyAsPieChartProps
    extends TradingPlatformStatisticCardLegacyCommon {
    hasColor?: never;
    hasPrefix?: never;
    value?: never;
    winLoss?: [number, number] | undefined;
    winPercent: number;
}

type TradingPlatformStatisticCardLegacyProps =
    | TradingPlatformStatisticCardLegacyAsValueProps
    | TradingPlatformStatisticCardLegacyAsPieChartProps;

export const TradingPlatformStatisticCardLegacy = memo(
    (props: TradingPlatformStatisticCardLegacyProps) => {
        const {
            hasColor = false,
            hasPrefix,
            isLoading,
            preferredColor,
            titleKey,
            value,
            winLoss,
            winPercent,
        } = props;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        const currentValue = useMemo(() => {
            if (winPercent != null) {
                return `${winPercent.toFixed(2)}%`;
            }

            if (!hasPrefix) {
                return value;
            }

            return parseNegativeNumber(+value, currency);
        }, [hasPrefix, winPercent, value, currency]);

        const colorize = useMemo(() => {
            if (!value || !hasColor) return preferredColor;

            return preferredColor || (+value >= 0 ? 'up' : 'down');
        }, [hasColor, value, preferredColor]);

        return (
            <Card wrapperClassName={cls.root}>
                <Flex
                    align="center"
                    justify="space-between"
                >
                    <Flex
                        gap={8}
                        vertical
                    >
                        <Typography.Text className={cls.title}>
                            {t(titleKey)}
                        </Typography.Text>
                        {isLoading ? (
                            <Skeleton.Input
                                active
                                className={cls.value}
                                size="small"
                            />
                        ) : (
                            <Typography.Text
                                className={cn(cls.value, {
                                    [cls.up]: colorize === 'up',
                                    [cls.down]: colorize === 'down',
                                })}
                            >
                                {currentValue}
                            </Typography.Text>
                        )}
                    </Flex>
                    {winPercent != null &&
                        (isLoading ? (
                            <Skeleton.Avatar
                                active
                                size={56}
                            />
                        ) : (
                            <TradingPlatformStatisticPieChart
                                titleKey={titleKey}
                                winLoss={winLoss}
                                winPercent={winPercent}
                            />
                        ))}
                </Flex>
            </Card>
        );
    },
);

TradingPlatformStatisticCardLegacy.displayName =
    'TradingPlatformStatisticCardLegacy';
